// InvoiceList.jsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    Button,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    FormGroup,
    Label,
    Input,
    Alert,
    UncontrolledTooltip,
    Spinner,
} from "reactstrap";
import {
    FaTrash,
    FaExclamationTriangle,
    FaCheck,
    FaTimes,
} from "react-icons/fa";
import { format } from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ReactBsTable from "components/Tables/ReactBsTable";
import NotificationAlert from "react-notification-alert";
import { listInvoices, updateInvoiceStatus, downloadInvoice } from "actions/ledger";
import { getPublisherList } from "actions/publisher";
import { showAlert } from "components/Common/commonFunctions";
import { useNavigate } from "react-router-dom";

// =============== MODAL STYLES ===============
const modalStyles = {
    header: {
        borderBottom: "2px solid #f0f0f0",
        paddingBottom: "18px",
        paddingTop: "18px",
    },
    title: {
        fontSize: "18px",
        fontWeight: "700",
        color: "#2c3e50",
        letterSpacing: "0.3px",
    },
    body: {
        padding: "24px",
        maxHeight: "calc(100vh - 200px)",
        overflowY: "auto",
    },
};

// =============== HELPERS ===============
const formatMonthDisplay = (monthString) => {
    if (!monthString) return "";
    try {
        const [year, month] = monthString.split("-");
        if (!year || !month) return monthString;
        const date = new Date(parseInt(year), parseInt(month) - 1, 1);
        return format(date, "MMMM yyyy");
    } catch (e) {
        return monthString;
    }
};

// =============== COMPONENT ===============
const InvoiceList = () => {
    const notificationAlertRef = useRef(null);
    const navigate = useNavigate();

    // ✅ No localStorage — always start with default (all months, all publishers)
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [selectedPublisher, setSelectedPublisher] = useState("");
    const [publisherOptions, setPublisherOptions] = useState([]);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [filteredInvoices, setFilteredInvoices] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const [filterText, setFilterText] = useState("");

    // Modals
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState("");

    const monthPickerStyles = `
        .styled-month-picker {
            border: none !important;
            border-radius: 16px !important;
            box-shadow: 0 18px 45px rgba(15, 23, 42, 0.16) !important;
            overflow: hidden !important;
            background: #ffffff !important;
        }
        .styled-month-picker .react-datepicker__header {
            background: linear-gradient(135deg, #3b3a3a 0%, #8a8a8a 100%) !important;
            border-bottom: none !important;
            padding-top: 14px !important;
        }
        .styled-month-picker .react-datepicker__current-month,
        .styled-month-picker .react-datepicker__time__header,
        .styled-month-picker .react-datepicker-year-header {
            color: #ffffff !important;
            font-weight: 700 !important;
        }
        .styled-month-picker .react-datepicker__navigation {
            top: 7px !important;
            align-items: center !important;
        }
        .styled-month-picker .react-datepicker__navigation-icon::before {
            border-color: #ffffff !important;
        }
        .styled-month-picker .react-datepicker__month-text {
            width: 88px !important;
            padding: 12px 0 !important;
            border-radius: 10px !important;
            margin: 5px 0 !important;
            font-size: 0.9rem !important;
            color: #243b53 !important;
        }
        .styled-month-picker .react-datepicker__month-text:hover {
            background: #cdcdcd !important;
            color: #121212 !important;
        }
        .styled-month-picker .react-datepicker__month-text--selected,
        .styled-month-picker .react-datepicker__month-text--keyboard-selected {
            background: linear-gradient(135deg, #a2a2a2 0%, #1a1818 100%) !important;
            color: #ffffff !important;
            box-shadow: 0 8px 18px rgba(143, 143, 143, 0.22) !important;
        }
        .styled-month-picker-popper {
            z-index: 1300 !important;
        }
    `;

    const notify = (type, message) => {
        if (notificationAlertRef.current) {
            notificationAlertRef.current.notificationAlert({
                place: "tr",
                message: <div>{message}</div>,
                type,
                icon: "tim-icons icon-bell-55",
                autoDismiss: 5,
            });
        }
    };

    // =============== FETCH INVOICES ===============
    const fetchInvoices = async (page, month, year, publisherId) => {
        try {
            setIsLoading(true);
            setInvoices([]);
            setFilteredInvoices([]);

            const filters = [];
            let targetMonth = month;
            let targetYear = year;

            if (publisherId && publisherId !== "") {
                targetMonth = null;
                targetYear = null;
                filters.push({
                    field: "PublisherID",
                    condition: "eq",
                    filterValues: [publisherId],
                });
            }

            const response = await listInvoices(page, targetMonth, targetYear, filters);

            if (response?.code === 200 && response?.data) {
                const data = response.data;
                const invoiceData = data.LedgerInvoice || data.data || data.invoices || [];

                const mappedInvoices = invoiceData.map((item) => ({
                    ID: item.ID || item.id,
                    publisherID: item.publisherID || item.publisher_id || item.publisherId,
                    ledgerID: item.ledgerID || item.ledger_id || item.ledgerId,
                    companyName: item.companyName || item.publisherName || item.publisher || `Publisher ${item.publisherID}`,
                    publisherStatus: item.publisherStatus || item.PublisherStatus || "",
                    invoiceNumber: item.invoiceNumber || item.InvoiceNumber || "",
                    invoicePath: item.invoicePath || item.InvoicePath || "",
                    uploadedBy: item.uploadedBy || item.UploadedBy || "",
                    uploadedByName: item.uploadedByName || "-",
                    uploadedOn:item.uploadedOn || "-",
                    rejectedReason: item.rejectedReason || item.RejectedReason || "",
                    status: item.status || item.Status || "PENDING",
                    month: item.ledgerMonth || item.month || item.Month || "",
                    monthDisplay: formatMonthDisplay(item.ledgerMonth || item.month || item.Month),
                    revenue: item.revenue || item.Revenue || 0,
                    netPayable: item.netPayable || item.NetPayable || 0,
                    adjustmentTotal: item.adjustmentTotal || item.AdjustmentTotal || 0,
                    advanceApplied: item.advanceApplied || item.AdvanceApplied || 0,
                    openingBalance: item.openingBalance || item.OpeningBalance || 0,
                    closingBalance: item.closingBalance || item.ClosingBalance || 0,
                    ledgerStatus: item.ledgerStatus || item.LedgerStatus || "",
                    createdBy: item.createdBy || "",
                    createdAt: item.createdAt || "",
                }));

                setInvoices(mappedInvoices);
                setFilteredInvoices(mappedInvoices);
                setTotalRecords(data.totalRecords || data.total_records || mappedInvoices.length);
            } else {
                setInvoices([]);
                setFilteredInvoices([]);
                setTotalRecords(0);
                notify("danger", response?.message || "No invoice data found");
            }
        } catch (error) {
            console.error("Error fetching invoices:", error);
            setInvoices([]);
            setFilteredInvoices([]);
            setTotalRecords(0);
            notify("danger", "Failed to fetch invoice data");
        } finally {
            setIsLoading(false);
        }
    };

    // =============== SINGLE SOURCE OF TRUTH FOR FETCHING ===============
    useEffect(() => {
        const month = selectedMonth ? parseInt(selectedMonth) : null;
        const year = selectedMonth ? currentYear : null;
        const publisherId = selectedPublisher || "";

        fetchInvoices(pageNum, month, year, publisherId);
        // eslint-disable-next-line
    }, [selectedMonth, selectedPublisher, currentYear, pageNum]);

    // Load publishers once
    useEffect(() => {
        const loadPublishers = async () => {
            try {
                const res = await getPublisherList(0, [], "", "", "");
                const opts = (res?.data?.Publisher || []).map((item) => ({
                    ID: item?.ID || item?.id || "",
                    label: item?.companyName || item?.name || "",
                    value: item?.ID || item?.id || "",
                }));
                setPublisherOptions(opts);
            } catch (e) {
                console.warn("Failed to load publishers", e);
            }
        };
        loadPublishers();
    }, []);

    // Client-side text filter only
    useEffect(() => {
        if (!filterText) {
            setFilteredInvoices(invoices);
            return;
        }
        const f = invoices.filter(inv =>
            (inv.companyName || "").toLowerCase().includes(filterText.toLowerCase()) ||
            (inv.invoiceNumber || "").toLowerCase().includes(filterText.toLowerCase())
        );
        setFilteredInvoices(f);
    }, [invoices, filterText]);

    // =============== HANDLERS ===============
    const handleMonthChange = (date) => {
        if (!date) {
            setSelectedMonth(null);
            setSelectedPublisher("");
            setPageNum(1);
            return;
        }
        setCurrentYear(date.getFullYear());
        setSelectedMonth(String(date.getMonth() + 1));
        setSelectedPublisher("");
        setPageNum(1);
    };

    const handlePublisherChange = (val) => {
        setSelectedPublisher(val);
        setPageNum(1);
        if (val) {
            setSelectedMonth(null); // Show all months for selected publisher
        }
    };

    const handleReset = () => {
        setSelectedPublisher("");
        setSelectedMonth(null);
        setFilterText("");
        setPageNum(1);
    };

    const handlePageChange = (page) => setPageNum(page);

    const handleSizePerPageChange = (e) => {
        setPageSize(Number(e.target.value));
        setPageNum(1);
    };

    const handleViewInvoice = (invoice) => {
        setSelectedInvoice(invoice);
        setIsViewModalOpen(true);
    };

    const downloadFile = (blobData, fileName, contentType) => {
        const blob = new Blob([blobData], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const handleDownloadInvoice = async (row) => {
        try {
            const res = await downloadInvoice(row.ID);

            if (res?.code && res.code !== 200) {
                const message = Array.isArray(res?.message)
                    ? res?.message[0]?.Msg
                    : res?.message || "Something went wrong";
                notify("danger", message);
                return;
            }

            let fileName = row.invoiceNumber || "invoice";
            let contentType = "application/octet-stream";

            const invoicePath = row.invoicePath || "";
            if (invoicePath) {
                const pathParts = invoicePath.split("/");
                const fullFileName = pathParts[pathParts.length - 1];
                if (fullFileName) {
                    const extension = fullFileName.split(".").pop().toLowerCase();
                    fileName = `${fileName}.${extension}`;
                    switch (extension) {
                        case "pdf": contentType = "application/pdf"; break;
                        case "png": contentType = "image/png"; break;
                        case "jpg":
                        case "jpeg": contentType = "image/jpeg"; break;
                        case "gif": contentType = "image/gif"; break;
                        case "doc": contentType = "application/msword"; break;
                        case "docx": contentType = "application/vnd.openxmlformats-officedocument.wordprocessingml.document"; break;
                        default: contentType = "application/octet-stream";
                    }
                }
            }

            if (res instanceof Blob || res instanceof ArrayBuffer || typeof res === "string") {
                downloadFile(res, fileName, contentType);
                notify("success", `Downloading ${fileName}`);
            } else {
                notify("danger", "Invalid file response");
            }
        } catch (error) {
            console.error("Download error:", error);
            notify("danger", "Failed to download invoice");
        }
    };

    const handleAcceptInvoice = async (invoice) => {
        showAlert({
            title: "Accept Invoice",
            message: `Are you sure you want to accept invoice "${invoice.invoiceNumber}" for ${invoice.companyName}?`,
            type: "success",
            confirmStyle: "secondary",
            cancelStyle: "btn btn-primary btn-gradient-primary",
            confirmText: "Yes, Accept",
            cancelText: "Cancel",
            onConfirm: async () => {
                try {
                    setIsLoading(true);
                    const response = await updateInvoiceStatus(invoice.ID, { status: "ACCEPTED" });
                    if (response?.code === 200) {
                        notify("success", `Invoice "${invoice.invoiceNumber}" accepted successfully!`);
                        const month = selectedMonth ? parseInt(selectedMonth) : null;
                        const year = selectedMonth ? currentYear : null;
                        await fetchInvoices(pageNum, month, year, selectedPublisher || "");
                    } else {
                        notify("danger", response?.message || "Failed to accept invoice");
                    }
                } catch (error) {
                    console.error("Error accepting invoice:", error);
                    notify("danger", "Something went wrong while accepting invoice");
                } finally {
                    setIsLoading(false);
                }
            },
            onCancel: () => { },
        });
    };

    const handleRejectInvoice = (invoice) => {
        setSelectedInvoice(invoice);
        setRejectReason("");
        setIsRejectModalOpen(true);
    };

    const handleConfirmReject = async () => {
        if (!rejectReason || rejectReason.trim() === "") {
            notify("danger", "Please provide a reason for rejection");
            return;
        }

        try {
            setIsLoading(true);
            const response = await updateInvoiceStatus(selectedInvoice.ID, {
                status: "REJECTED",
                rejectedReason: rejectReason.trim()
            });
            if (response?.code === 200) {
                notify("success", `Invoice "${selectedInvoice.invoiceNumber}" rejected successfully!`);
                setIsRejectModalOpen(false);
                setRejectReason("");
                setSelectedInvoice(null);
                const month = selectedMonth ? parseInt(selectedMonth) : null;
                const year = selectedMonth ? currentYear : null;
                await fetchInvoices(pageNum, month, year, selectedPublisher || "");
            } else {
                notify("danger", response?.message || "Failed to reject invoice");
            }
        } catch (error) {
            console.error("Error rejecting invoice:", error);
            notify("danger", "Something went wrong while rejecting invoice");
        } finally {
            setIsLoading(false);
        }
    };

    // =============== RENDER HELPERS ===============
    const getStatusBadge = (status) => {
        const statusMap = {
            PENDING: { color: "warning", label: "Pending" },
            ACCEPTED: { color: "success", label: "Accepted" },
            REJECTED: { color: "danger", label: "Rejected" },
            OPEN: { color: "warning", label: "Open" },
            PAID: { color: "success", label: "Paid" },
            VOID: { color: "danger", label: "Void" },
            LOCK: { color: "primary", label: "Locked" },
        };
        const s = statusMap[status] || statusMap.PENDING;
        return <Badge color={s.color}>{s.label}</Badge>;
    };

    const getLedgerStatusBadge = (status) => {
        const statusMap = {
            OPEN: { color: "warning", label: "Open" },
            POSTED: { color: "info", label: "Posted" },
            LOCK: { color: "primary", label: "Locked" },
            PAID: { color: "success", label: "Paid" },
            RERUN: { color: "danger", label: "Rerun" },
        };
        const s = statusMap[status] || statusMap.OPEN;
        return <Badge color={s.color}>{s.label}</Badge>;
    };

    const formatCurrency = (amount) => {
        if (amount === undefined || amount === null || isNaN(amount)) return "$0.00";
        return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
    };

    const handleRowClick = (row) => {
        navigate("/admin/invoices/details", { state: row });
    };

    // =============== TABLE COLUMNS ===============
    const columns = useMemo(() => [
        {
            dataField: "companyName",
            text: "Publisher",
            formatter: (cell, row) => (
                <div>
                    <strong>{row.companyName}</strong>
                    <br />
                    <small className="text-muted">Status: {row.publisherStatus || "-"}</small>
                </div>
            ),
            sort: true,
        },
        {
            dataField: "invoiceNumber",
            text: "Invoice Number",
            formatter: (cell, row) => <strong>{row.invoiceNumber || "-"}</strong>,
            sort: true,
        },
        {
            dataField: "monthDisplay",
            text: "Month",
            formatter: (cell) => cell || "-",
            sort: true,
        },
        {
            dataField: "revenue",
            text: "Revenue",
            formatter: (cell) => formatCurrency(cell),
            headerAlign: "right",
            align: "right",
            sort: true,
        },
        {
            dataField: "adjustmentTotal",
            text: "Adjustments",
            formatter: (cell) => formatCurrency(cell || 0),
            headerAlign: "right",
            align: "right",
            sort: true,
        },
        {
            dataField: "netPayable",
            text: "Net Payable",
            formatter: (cell) => <span className="fw-bold">{formatCurrency(cell)}</span>,
            headerAlign: "right",
            align: "right",
            sort: true,
        },
        {
            dataField: "status",
            text: "Invoice Status",
            formatter: (cell) => getStatusBadge(cell),
            sort: true,
        },
        {
            dataField: "ledgerStatus",
            text: "Ledger Status",
            formatter: (cell) => getLedgerStatusBadge(cell),
            sort: true,
        },
        {
            dataField: "uploadedByName",
            text: "Uploaded By",
            formatter: (cell) => cell || "-",
            sort: true,
        },
                {
            dataField: "uploadedOn",
            text: "Uploaded On",
            formatter: (cell) => cell || "-",
            sort: true,
        },
        {
            dataField: "actions",
            text: "Actions",
            formatter: (cell, row) => {
                const rowId = row.ID || row.id;
                const isPending = row.status === "PENDING";
                return (
                    <div style={{ display: "flex", gap: 8, justifyContent: "center", alignItems: "center", flexWrap: "wrap" }}>
                        <a className="table-action table-action-info" id={`view-${rowId}`}
                            onClick={(e) => { e.stopPropagation(); handleViewInvoice(row); }}>
                            <i className="fas fa-eye" />
                        </a>
                        <UncontrolledTooltip delay={0} target={`view-${rowId}`}>View Invoice</UncontrolledTooltip>

                        <a className="table-action table-action-info" id={`download-${rowId}`}
                            onClick={(e) => { e.stopPropagation(); handleDownloadInvoice(row); }}>
                            <i className="fas fa-download" />
                        </a>
                        <UncontrolledTooltip delay={0} target={`download-${rowId}`}>Download Invoice</UncontrolledTooltip>

                        {isPending && (
                            <>
                                <a className="table-action table-action-success" id={`accept-${rowId}`}
                                    onClick={(e) => { e.stopPropagation(); handleAcceptInvoice(row); }}>
                                    <i className="fas fa-check" />
                                </a>
                                <UncontrolledTooltip delay={0} target={`accept-${rowId}`}>Accept Invoice</UncontrolledTooltip>

                                <a className="table-action table-action-danger" id={`reject-${rowId}`}
                                    onClick={(e) => { e.stopPropagation(); handleRejectInvoice(row); }}>
                                    <i className="fas fa-times" />
                                </a>
                                <UncontrolledTooltip delay={0} target={`reject-${rowId}`}>Reject Invoice</UncontrolledTooltip>
                            </>
                        )}
                    </div>
                );
            },
            headerAlign: "center",
            align: "center",
        }
    ], [selectedMonth, currentYear, selectedPublisher, pageNum]);

    // =============== FILTER SECTION ===============
    const filterSection = () => (
        <Row className="align-items-center">
            <Col md={3}>
                <FormGroup>
                    <DatePicker
                        selected={selectedMonth ? new Date(currentYear, Number(selectedMonth) - 1, 1) : null}
                        onChange={handleMonthChange}
                        className="form-control"
                        calendarClassName="styled-month-picker"
                        popperClassName="styled-month-picker-popper"
                        dateFormat="MMMM yyyy"
                        showMonthYearPicker
                        placeholderText="Select Month / Year"
                        isClearable
                        disabled={!!selectedPublisher}
                    />
                </FormGroup>
            </Col>
            <Col md={3}>
                <FormGroup>
                    <Input
                        type="select"
                        value={selectedPublisher}
                        onChange={(e) => handlePublisherChange(e.target.value)}
                    >
                        <option value="">All Publishers</option>
                        {publisherOptions.map(p => (
                            <option key={p.ID} value={p.ID}>{p.label}</option>
                        ))}
                    </Input>
                </FormGroup>
            </Col>
            <Col md={3}>
                <FormGroup>
                    <Input
                        placeholder="Search by publisher or invoice..."
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </FormGroup>
            </Col>
            <Col md={3} className="d-flex align-items-center gap-2 flex-wrap">
                <Button className="secondary" onClick={handleReset}>Reset</Button>
            </Col>
        </Row>
    );

    // =============== RENDER ===============
    return (
        <>
            <style>{monthPickerStyles}</style>
            <div className="rna-container">
                <NotificationAlert ref={notificationAlertRef} />
            </div>
            <Container className="mt--6" fluid>
                <Card>
                    <ReactBsTable
                        title="Invoices"
                        description="Manage publisher invoices"
                        data={filteredInvoices}
                        columns={columns}
                        keyField="ID"
                        handleRowClick={handleRowClick}
                        renderFilters={filterSection}
                        page={pageNum}
                        showAddbtn={false}
                        sizePerPage={pageSize}
                        totalRecords={totalRecords}
                        onPageChange={handlePageChange}
                        showLoader={isLoading}
                        handleSizePerPageChange={handleSizePerPageChange}
                        bordered
                        hover
                    />
                </Card>

                {/* View Invoice Modal */}
                <Modal isOpen={isViewModalOpen} toggle={() => setIsViewModalOpen(false)} centered size="xl">
                    <ModalHeader toggle={() => setIsViewModalOpen(false)} style={modalStyles.header}>
                        <h5 style={modalStyles.title}>Tax Invoice</h5>
                    </ModalHeader>
                    <ModalBody style={modalStyles.body}>
                        {selectedInvoice && (
                            <div className="invoice-preview p-4">
                                {selectedInvoice?.invoicePath && (
                                    <div style={{ height: '70vh', marginTop: 16 }}>
                                        <iframe
                                            src={selectedInvoice.invoicePath}
                                            title="Invoice Preview"
                                            style={{ width: '100%', height: '100%', border: 'none' }}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setIsViewModalOpen(false)}>Close</Button>
                    </ModalFooter>
                </Modal>

                {/* Reject Modal */}
                <Modal isOpen={isRejectModalOpen} toggle={() => {
                    setIsRejectModalOpen(false);
                    setRejectReason("");
                    setSelectedInvoice(null);
                }} centered size="lg">
                    <ModalHeader toggle={() => {
                        setIsRejectModalOpen(false);
                        setRejectReason("");
                        setSelectedInvoice(null);
                    }} style={modalStyles.header}>
                        <h5 style={modalStyles.title}>Reject Invoice</h5>
                    </ModalHeader>
                    <ModalBody style={modalStyles.body}>
                        {selectedInvoice && (
                            <>
                              <Alert className="filter-btn-gradient-primary">
                                                         <FaExclamationTriangle className="me-2" />
                                    <strong>You are about to reject invoice:</strong>
                                    <ul className="mb-0 mt-2">
                                        <li><strong>Invoice Number:</strong> {selectedInvoice.invoiceNumber}</li>
                                        <li><strong>Publisher:</strong> {selectedInvoice.companyName}</li>
                                        <li><strong>Month:</strong> {selectedInvoice.monthDisplay}</li>
                                    </ul>
                                                      </Alert>
                                <FormGroup>
                                    <Label>Reason for Rejection <span className="text-danger">*</span></Label>
                                    <Input
                                        type="textarea"
                                        placeholder="Please provide a reason for rejecting this invoice..."
                                        value={rejectReason}
                                        onChange={(e) => setRejectReason(e.target.value)}
                                        rows={4}
                                    />
                                </FormGroup>
                            </>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button className="btn-gradient-primary" onClick={() => {
                            setIsRejectModalOpen(false);
                            setRejectReason("");
                            setSelectedInvoice(null);
                        }}>Cancel</Button>
                        <Button color="secondary" onClick={handleConfirmReject}
                            disabled={!rejectReason || rejectReason.trim() === "" || isLoading}>
                            {isLoading ? <><Spinner size="sm" className="me-1" /> Processing...</> : "Reject Invoice"}
                        </Button>
                    </ModalFooter>
                </Modal>
            </Container>
        </>
    );
};

export default InvoiceList;